import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, MessageSquare } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { ContactDropdown } from './ContactDropdown';

export const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Ceramic Coating', path: '/ceramic-coating-fresno' },
    { name: 'Paint Correction', path: '/paint-correction-fresno' },
    { name: 'Auto Detailing', path: '/mobile-detailing-fresno' },
  ];

  const scrollToSection = (sectionId) => {
    if (location.pathname !== '/') {
      window.location.href = `/#${sectionId}`;
    } else {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  return (
    <nav 
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-black/95 border-b border-white/10' : 'bg-transparent'
      }`}
    >
      <div className="container-custom">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center" data-testid="logo-link">
            <img 
              src={process.env.PUBLIC_URL + '/logo.png'}
              alt="Noble Detailing - Premium Auto Detailing Fresno"
              className="h-14 md:h-16 w-auto"
              style={{ mixBlendMode: 'lighten' }}
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`nav-link text-sm uppercase tracking-widest transition-colors ${
                  location.pathname === link.path ? 'text-cyan-400' : 'text-white/80 hover:text-cyan-400'
                }`}
                data-testid={`nav-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {link.name}
              </Link>
            ))}
            <button 
              onClick={() => scrollToSection('packages')}
              className="text-sm uppercase tracking-widest text-white/80 hover:text-cyan-400 transition-colors"
              data-testid="nav-packages"
            >
              Packages
            </button>
          </div>

          {/* CTA Button */}
          <div className="hidden lg:flex items-center gap-4">
            <ContactDropdown 
              label="Contact Us" 
              className="text-sm text-cyan-400 hover:text-cyan-300 transition-colors font-accent font-semibold border border-cyan-500/30 px-4 py-2 rounded-sm"
              testId="nav-contact-dropdown"
            />
            <button 
              onClick={() => scrollToSection('contact')}
              className="btn-primary text-sm"
              data-testid="book-now-btn"
            >
              Book Now
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 text-white"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-black border-t border-white/10"
            data-testid="mobile-menu"
          >
            <div className="container-custom py-6 flex flex-col gap-4">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`text-lg uppercase tracking-wider py-2 ${
                    location.pathname === link.path ? 'text-cyan-400' : 'text-white/80'
                  }`}
                  data-testid={`mobile-nav-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {link.name}
                </Link>
              ))}
              <button 
                onClick={() => scrollToSection('packages')}
                className="text-lg uppercase tracking-wider py-2 text-white/80 text-left"
                data-testid="mobile-nav-packages"
              >
                Packages
              </button>
              <hr className="border-white/10 my-2" />
              <a 
                href="sms:5598801818?&body=Hi%2C%20I%27d%20like%20to%20get%20a%20quote%20for%20detailing%20services." 
                className="flex items-center gap-2 text-cyan-400 py-2"
                data-testid="mobile-text-link"
              >
                <MessageSquare className="w-5 h-5" />
                <span className="font-accent font-semibold text-lg">Text Us</span>
              </a>
              <a 
                href="tel:5598801818" 
                className="flex items-center gap-2 text-cyan-400 py-2"
                data-testid="mobile-phone-link"
              >
                <Phone className="w-5 h-5" />
                <span className="font-accent font-semibold text-lg">Call 559-880-1818</span>
              </a>
              <button 
                onClick={() => scrollToSection('contact')}
                className="btn-primary w-full mt-2"
                data-testid="mobile-book-btn"
              >
                Book Now
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};
